import {
  ActionPresetDef,
  AssetAnimationManifestEntry,
  DataSourcesConfig,
  DialogueDef,
  GameDataBundle,
  HouseDef,
  NPCDef,
  PortraitManifestEntry,
  QuestDef,
  VillageDef,
} from '../types/data';
import { DebugLog } from './DebugLog';

const DEFAULT_DATA_SOURCES: DataSourcesConfig = {
  mode: 'local_first',
  remoteBaseUrl: '',
  files: {
    villages: '/data/villages.json',
    houses: '/data/houses.json',
    npcs: '/data/npcs.json',
    dialogues: '/data/dialogues.json',
    quests: '/data/quests.json',
  },
};

async function loadJson<T>(path: string): Promise<T> {
  DebugLog.info('Loader', `fetch ${path}`);
  const response = await fetch(path, { cache: 'no-store' });
  if (!response.ok) {
    DebugLog.error('Loader', `fetch failed ${path}`, { status: response.status });
    throw new Error(`JSON load failed: ${path}`);
  }
  DebugLog.info('Loader', `fetch ok ${path}`);
  return response.json() as Promise<T>;
}

async function loadDataSourcesConfig(): Promise<DataSourcesConfig> {
  try {
    return await loadJson<DataSourcesConfig>('/config/data-sources.json');
  } catch {
    DebugLog.warn('Loader', 'data-sources config missing, using defaults');
    return DEFAULT_DATA_SOURCES;
  }
}

function buildRemoteUrl(base: string | undefined, path: string): string {
  if (!base) throw new Error('remoteBaseUrl is empty');
  return `${base.replace(/\/$/, '')}${path}`;
}

async function loadBundleFrom(baseUrl: string | undefined, files: DataSourcesConfig['files']): Promise<GameDataBundle> {
  const resolve = (path: string) => (baseUrl ? buildRemoteUrl(baseUrl, path) : path);

  const [villages, houses, npcs, dialogues, quests, actionPresets] = await Promise.all([
    loadJson<VillageDef[]>(resolve(files.villages)),
    loadJson<HouseDef[]>(resolve(files.houses)),
    loadJson<NPCDef[]>(resolve(files.npcs)),
    loadJson<DialogueDef[]>(resolve(files.dialogues)),
    loadJson<QuestDef[]>(resolve(files.quests ?? '/data/quests.json')),
    loadJson<ActionPresetDef[]>(resolve('/data/action-presets.json')),
  ]);

  return {
    villages,
    houses,
    npcs,
    dialogues,
    quests,
    actionPresets,
    sourceInfo: baseUrl ? `remote: ${baseUrl}` : 'local bundled json',
  };
}

export async function loadPortraitManifest(): Promise<PortraitManifestEntry[]> {
  try {
    return await loadJson<PortraitManifestEntry[]>('/data/portrait-manifest.json');
  } catch (error) {
    DebugLog.warn('Loader', 'portrait manifest missing', error);
    return [];
  }
}

export async function loadAnimationManifest(): Promise<AssetAnimationManifestEntry[]> {
  try {
    return await loadJson<AssetAnimationManifestEntry[]>('/data/animation-manifest.json');
  } catch (error) {
    DebugLog.warn('Loader', 'animation manifest missing', error);
    return [];
  }
}

export async function loadGameData(): Promise<GameDataBundle> {
  const cfg = await loadDataSourcesConfig();
  const tries = cfg.mode === 'remote_first'
    ? [() => loadBundleFrom(cfg.remoteBaseUrl, cfg.files), () => loadBundleFrom(undefined, cfg.files)]
    : [() => loadBundleFrom(undefined, cfg.files), () => loadBundleFrom(cfg.remoteBaseUrl, cfg.files)];

  let lastError: unknown;
  for (const attempt of tries) {
    try {
      DebugLog.info('Loader', 'bundle attempt start', { mode: cfg.mode, remoteBaseUrl: cfg.remoteBaseUrl });
      const bundle = await attempt();
      DebugLog.info('Loader', 'bundle attempt success', {
        villages: bundle.villages.length,
        houses: bundle.houses.length,
        npcs: bundle.npcs.length,
        dialogues: bundle.dialogues.length,
        quests: bundle.quests.length,
      });
      return bundle;
    } catch (error) {
      lastError = error;
      DebugLog.warn('Loader', 'bundle attempt failed', error);
    }
  }

  DebugLog.error('Loader', 'loadGameData failed', lastError);
  throw lastError instanceof Error ? lastError : new Error('Failed to load game data');
}
